/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {ContactDetailsFilter} from '@agent-assist/api-typescript-react-query-hooks';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import {Box, Button, Card, CardContent, Stack} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {FilterOptionsComplete, type OnFilterParams} from './contact-import/FilterOptions';
import {FilterItem} from './settings/MyTeamPage';
import {AIInsightClassifications} from '../components/Analytics/AIInsightClassifications';
import {AIInsightVisualisation} from '../components/Analytics/AIInsightVisualisations';
import {availableFilters} from '../components/Analytics/constants';
import {MyTeamFilterModal} from '../components/Analytics/MyTeamFilterModal';

import {PageContainer} from '../components/PageContainer';
import {PromptRevisionScores} from '../components/QAAnalytics/PromptRevisionScores';
import {SelectCriteria} from '../components/SelectContactImport/SelectCriteria';

export const AnalyticsPage = () => {
  const params = useParams();
  const paramCriteriaId = params.criteriaId;
  const criteriaId = paramCriteriaId ?? '_DEFAULT_PROMPT';

  const [isTeamsUsersModalOpen, setIsTeamsUsersModalOpen] = useState(false);
  const [manualFilters, setManualFilters] = useState<ContactDetailsFilter[]>([]);
  const [teamsAndUsersFilter, setTeamsAndUsersFilter] = useState<FilterItem[]>([]);
  const [filters, setFilters] = useState<ContactDetailsFilter[]>([]);

  useEffect(() => {
    const teamsAndUsersFilterWithKey = teamsAndUsersFilter.map((item) => ({
      ...item,
      key: 'userIds',
    }));
    setFilters([...manualFilters, ...teamsAndUsersFilterWithKey]);
  }, [teamsAndUsersFilter, manualFilters]);

  const handleSetFilter = (paramOrParams: OnFilterParams) => {
    const newParamOrParams = paramOrParams.map((param) => ({...param, operation: 'EQUALS_OPERATOR'}));
    setManualFilters(newParamOrParams as ContactDetailsFilter[]);
  };

  const handleDeleteAllFilters = () => {
    setManualFilters([]);
    setTeamsAndUsersFilter([]);
  };

  const handleDeleteFilter = (key: string, id?: string) => {
    const newManualFilters = manualFilters.filter((filter) => filter.key !== key);

    setManualFilters(newManualFilters);

    const newTeamsAndUsersFilter = teamsAndUsersFilter.filter((filter) => filter.id !== id);
    setTeamsAndUsersFilter(newTeamsAndUsersFilter);
  };

  const commonProps = {
    criteriaId: criteriaId,
    filters: filters,
  };

  const handleTeamFilterApply = (members: FilterItem[]) => {
    setTeamsAndUsersFilter(members);
  };

  const pageActions = (
    <>
      <Button variant={'outlined'} startIcon={<PeopleAltIcon />} onClick={() => setIsTeamsUsersModalOpen(true)}>
        My Team
      </Button>
      <Box>
        <SelectCriteria initialValue={criteriaId} />
      </Box>
    </>
  );

  return (
    <PageContainer title={'Insights and Analytics'} actions={pageActions}>
      <Card>
        <CardContent>
          <FilterOptionsComplete
            activeFilters={filters}
            availableFilters={availableFilters}
            criteriaId={criteriaId}
            handleSetFilter={handleSetFilter}
            handleDeleteAllFilters={handleDeleteAllFilters}
            handleDeleteFilter={handleDeleteFilter}
          />
        </CardContent>
      </Card>
      <Grid container spacing={4} columns={2}>
        <Grid size={1}>
          <Box>
            <AIInsightClassifications commonProps={commonProps} />
          </Box>
        </Grid>
        <Grid size={1}>
          <Stack spacing={4}>
            <Box>
              <PromptRevisionScores criteriaId={criteriaId} filters={filters} />
            </Box>
            <Box>
              <AIInsightVisualisation commonProps={commonProps} />
            </Box>
          </Stack>
        </Grid>
      </Grid>
      {isTeamsUsersModalOpen && (
        <MyTeamFilterModal
          open={isTeamsUsersModalOpen}
          onClose={() => setIsTeamsUsersModalOpen(false)}
          onApply={handleTeamFilterApply}
          initialSelectedFilters={teamsAndUsersFilter}
        />
      )}
    </PageContainer>
  );
};
